import { Chip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EmailIcon from "@mui/icons-material/Email";
import { FileSpreadsheet, FileText } from "lucide-react";
import { EmailAttachment } from "@services/documents/email/types";

interface AttachmentChipProps {
  attachment: EmailAttachment;
  messageId: string;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Download an attachment from an email
 */
export const handleDownload = (messageId: string, attachment: EmailAttachment) => {
  if (attachment.content) {
    try {
      // Decode Base64 content into binary
      const binaryString = atob(attachment.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create Blob and trigger download
      const blob = new Blob([bytes], { type: attachment.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to decode Base64 content:", err);
    }
  } else {
    // Fallback to server-side download
    window.open(
      `/documents/email/attachment/${messageId}/${encodeURIComponent(attachment.filename)}`,
      "_blank"
    );
  }
};

/**
 * Get the appropriate icon for an attachment based on its content type
 */
const getAttachmentIcon = (contentType: string) => {
  const type = contentType.toLowerCase();

  // Email attachments
  if (type === "message/rfc822" || type.includes("email")) {
    return <EmailIcon />;
  }

  // PDF files
  if (type.includes("pdf")) {
    return <FileText size={20} />;
  }

  // Excel files
  if (
    type.includes("spreadsheet") ||
    type.includes("excel") ||
    type.includes("sheet")
  ) {
    return <FileSpreadsheet size={20} />;
  }

  // Default icon for other files
  return <AttachFileIcon />;
};

/**
 * AttachmentChip component that displays an attachment with the appropriate icon
 */
export const EmailAttachmentChip: React.FC<AttachmentChipProps> = ({
  attachment,
  messageId,
  onClick,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
    } else {
      e.stopPropagation();
      handleDownload(messageId, attachment);
    }
  };

  return (
    <Chip
      key={attachment.filename}
      icon={getAttachmentIcon(attachment.contentType)}
      label={attachment.filename}
      onClick={handleClick}
      sx={{ cursor: "pointer" }}
    />
  );
};
